// utils/formatCityStateZip.ts
// Restored original behavior (parsing city/state/zip) + street handling:
// - If input is an object, we read known fields.
// - If input is a string "Street, City, ST, ZIP", we parse parts.
// - We return: "<streetWithoutFirstToken>, <City>, <ST> <ZIP>"
//   where streetWithoutFirstToken = street with its first word removed.
//
// Examples:
// "123 N Main St, Bothell, WA, 98011" -> "N Main St, Bothell, WA 98011"
// "500 Fifth Ave, Seattle, WA 98101"  -> "Fifth Ave, Seattle, WA 98101"
// "Main St, Seattle, WA"              -> "St, Seattle, WA" (no numeric guard per spec)

export default function formatCityStateZip(addr: unknown): string {
  if (!addr) return "";

  // --- helpers ---
  const removeFirstToken = (s: string): string => {
    const cleaned = s.trim().replace(/\s+/g, " ");
    if (!cleaned) return "";
    const tokens = cleaned.split(" ");
    if (tokens.length <= 1) return "";
    const [, ...rest] = tokens;
    return rest.join(" ").trim();
  };

  const zipRe = /^\d{5}(?:-\d{4})?$/; // 12345 or 12345-6789
  const stateRe = /^[A-Za-z]{2}$/; // WA, OR, CA, etc.

  // --- case 1: structured object ---
  if (typeof addr === "object") {
    const a = addr as Record<string, any>;
    const streetRaw =
      a.street || a.address1 || a.addressLine1 || a.address || a.line1 || "";

    const city = a.city || a.locality || a.town || a.municipality || "";
    const state = a.state || a.stateCode || "";
    const zip = a.postalCode || a.zip || a.zipcode || "";

    const street = removeFirstToken(String(streetRaw || ""));

    // Build the right-hand part: "City, ST ZIP" with graceful fallbacks
    let right = "";
    if (city && state && zip)
      right = `${city}, ${String(state).toUpperCase()} ${zip}`;
    else if (city && state) right = `${city}, ${String(state).toUpperCase()}`;
    else if (city && zip) right = `${city} ${zip}`;
    else if (city) right = city;

    // If we have street and right, join with comma; else return whichever exists
    if (street && right) return `${street}, ${right}`;
    if (street) return street;
    return right || "";
  }

  // --- case 2: string path e.g., "Street, City, ST, ZIP" ---
  const parts = String(addr)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (!parts.length) return "";

  const last = parts[parts.length - 1] || "";
  const prev = parts[parts.length - 2] || "";
  const third = parts[parts.length - 3] || "";

  // Attempt to identify (City, ST, ZIP) suffix patterns
  let city = "";
  let state = "";
  let zip = "";
  let streetPart = "";

  // Pattern: "... Street, City, ST, ZIP"
  if (zipRe.test(last) && stateRe.test(prev) && third) {
    city = third;
    state = prev.toUpperCase();
    zip = last;
    // Everything before the last three is street (usually just the first chunk)
    streetPart = parts
      .slice(0, parts.length - 3)
      .join(", ")
      .trim();
  }
  // Pattern: "... Street, City, ST"
  else if (stateRe.test(last) && prev) {
    city = prev;
    state = last.toUpperCase();
    streetPart = parts
      .slice(0, parts.length - 2)
      .join(", ")
      .trim();
  }
  // Fallback: treat the first chunk as street, best-effort for the rest
  else {
    streetPart = parts[0] || "";
    // Try to guess city/state if present (best-effort)
    // If there are 2+ parts, assume next is city, then maybe state/zip
    if (parts.length >= 2) city = parts[1] || "";
    if (parts.length >= 3) state = (parts[2] || "").toUpperCase();
    if (parts.length >= 4) zip = parts[3] || "";
  }

  const street = removeFirstToken(streetPart);

  // Build "City, ST ZIP"
  let right = "";
  if (city && state && zip) right = `${city}, ${state} ${zip}`;
  else if (city && state) right = `${city}, ${state}`;
  else if (city && zip) right = `${city} ${zip}`;
  else if (city) right = city;

  if (state.toLocaleLowerCase() === "wa") return right;
  if (street && right) return `${street}, ${right}`;
  if (street) return street;
  return right || "";
}
